import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import TaskSelector from './TaskSelector';
import { LoadingIndicator } from './assets';

const dialogPaperSx = (theme) => ({
  position: 'relative',
  maxHeight: '85vh',
  overflow: 'hidden',
  border: `2px solid ${theme.palette.primary.main}`,
  borderRadius: 3,
  background:
    `radial-gradient(circle at 10% 0%, ${alpha(theme.palette.primary.main, 0.13)} 0%, transparent 34%), ` +
    `radial-gradient(circle at 100% 100%, ${alpha(theme.palette.secondary.main, 0.1)} 0%, transparent 38%), ` +
    `linear-gradient(155deg, ${theme.palette.background.paper} 0%, #101915 100%)`,
  boxShadow:
    `0 0 0 1px ${alpha(theme.palette.green?.main || '#39FF14', 0.24)}, ` +
    `0 0 24px ${alpha(theme.palette.primary.main, 0.3)}, ` +
    `0 20px 60px rgba(0, 0, 0, 0.72), ` +
    `0 0 60px ${alpha(theme.palette.secondary.main, 0.1)}`,
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: '0 14% auto',
    height: 3,
    background:
      `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, ` +
      `${theme.palette.green?.main || '#39FF14'}, ${theme.palette.secondary.main}, transparent)`,
    boxShadow: `0 0 14px ${theme.palette.primary.main}`,
    zIndex: 2,
  },
});

const TaskSelectionDialog = ({
  idPrefix = 'task-selection',
  open,
  onClose,
  onConfirm,
  tasks,
  title,
  confirmLabel,
  error = null,
  isLoading = false,
  isSubmitting = false,
  submittingText = 'working',
}) => {
  const [selectedTaskId, setSelectedTaskId] = useState(null);

  useEffect(() => {
    if (open) setSelectedTaskId(null);
  }, [open]);

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  if (isSubmitting) {
    return (
      <Dialog
        id={`${idPrefix}-dialog`}
        open={open}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: dialogPaperSx } }}
      >
        <Box
          sx={{
            minHeight: 360,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
          }}
        >
          <LoadingIndicator text={submittingText} />
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog
      id={`${idPrefix}-dialog`}
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: dialogPaperSx,
        },
      }}
    >
      <DialogTitle
        id={`${idPrefix}-title`}
        sx={(theme) => ({
          color: theme.palette.green?.main || '#39FF14',
          textAlign: 'center',
          textTransform: 'uppercase',
          letterSpacing: { xs: 0.5, sm: 1.5 },
          textShadow: `0 0 12px ${alpha(theme.palette.green?.main || '#39FF14', 0.55)}`,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
          background:
            `linear-gradient(90deg, transparent, ${alpha(theme.palette.primary.main, 0.07)}, transparent)`,
          py: 2.5,
        })}
      >
        {title}
      </DialogTitle>
      <DialogContent
        id={`${idPrefix}-content`}
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          pt: '16px !important',
          backgroundImage:
            `linear-gradient(${alpha(theme.palette.primary.main, 0.025)} 1px, transparent 1px), ` +
            `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.025)} 1px, transparent 1px)`,
          backgroundSize: '24px 24px',
        })}
      >
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {isLoading ? (
          <Box
            sx={{
              minHeight: 260,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LoadingIndicator text="loading tasks" />
          </Box>
        ) : (
          <TaskSelector
            idPrefix={idPrefix}
            taskData={tasks}
            selectedTaskId={selectedTaskId}
            onTaskSelect={setSelectedTaskId}
          />
        )}
      </DialogContent>
      <DialogActions
        id={`${idPrefix}-actions`}
        sx={(theme) => ({
          borderTop: `1px solid ${alpha(theme.palette.secondary.main, 0.22)}`,
          background: alpha(theme.palette.background.default, 0.72),
          px: 3,
          py: 1.5,
        })}
      >
        <Button id={`${idPrefix}-cancel`} onClick={handleClose}>Cancel</Button>
        <Button
          id={`${idPrefix}-confirm`}
          variant="contained"
          disabled={isLoading || !selectedTaskId}
          onClick={() => onConfirm(selectedTaskId)}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskSelectionDialog;
